import React, { useState, useEffect, useRef } from 'react';
import FileUpload from '../../components/FileUpload/FileUpload';
import styles from './SubmitInvoicePage.module.css'
import SubmitInvoiceForm from '../../components/SubmitInvoiceForm/SubmitInvoiceForm';
import Modal from 'react-modal';
import { useNavigate, useParams } from 'react-router-dom';
import { Home } from '@mui/icons-material';
import CreateIcon from '@mui/icons-material/Create';
import { PDFDocument } from 'pdf-lib';
import { UPLOAD_INVOICE, GET_INVOICE, MODIFY_INVOICE, GET_MY_PROFILE } from '../../services/graphql';
import { useMutation, useQuery } from '@apollo/client';
import { numericInputError, normalizeNumericInput } from '../../utils/numericInput';
import InvoiceSubmissionTabMenu from '../../components/InvoiceSubmissionTabMenu/InvoiceSubmissionTabMenu';
import { useSelector } from 'react-redux';

// Required fields that hold plain text, paired with the label the user sees.
const REQUIRED_TEXT_FIELDS = [
    ['expenseType', 'Expense Type'],
    ['category', 'Category'],
    ['subCategory', 'Sub-category'],
    ['entity', 'Entity'],
    ['currency', 'Currency'],
];

const SubmitInvoicePage = () => {
    const PAYMENT_DETAILS_REQUIRED_MESSAGE = 'Update your payment details in My Profile section to be able to submit invoices.';
    const navigate = useNavigate();
    const { invoiceID: routeInvoiceID } = useParams();
    const isEditMode = Boolean(routeInvoiceID);
    const currentUserEmail = useSelector((state) => state.graphql.email);

    const customModalStyles = {
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'var(--theme-bg-elevated)',
        },
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
    };

    const [tabs, setTabs] = useState([{ id: 1, title: 'Default Invoice 1' }]);
    const [activeTab, setActiveTab] = useState(1);
    const [invoiceDataSubmitted, setInvoiceDataSubmitted] = useState(false);

    const [fileUploadData, setFileUploadData] = useState([]);
    const [submitInvoiceFormData, setSubmitInvoiceFormData] = useState([]);

    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [prefillExpenseType, setPrefillExpenseType] = useState('');
    const [prefillCategory, setPrefillCategory] = useState('');
    const [prefillProject, setPrefillProject] = useState('');

    const [editOriginalInvoice, setEditOriginalInvoice] = useState(null);
    const [editInitialFormData, setEditInitialFormData] = useState(null);
    const [editFormData, setEditFormData] = useState(null);
    const [editFileUploadData, setEditFileUploadData] = useState([]);
    const [removeExistingFile, setRemoveExistingFile] = useState(false);
    const [isDocumentDrawerOpen, setIsDocumentDrawerOpen] = useState(false);

    const failedInvoicesRef = useRef([]);
    const { data: currentUserProfileData } = useQuery(GET_MY_PROFILE, {
        skip: !currentUserEmail,
        variables: { email: currentUserEmail },
    });
    const rawCanSubmitDistanceInvoices = currentUserProfileData?.getUserProfile?.body?.canSubmitDistanceInvoices;
    const canSubmitDistanceInvoices =
        rawCanSubmitDistanceInvoices === undefined ||
        rawCanSubmitDistanceInvoices === null ||
        rawCanSubmitDistanceInvoices === true ||
        rawCanSubmitDistanceInvoices === 1;
    const profileBody = currentUserProfileData?.getUserProfile?.body;
    const customDistanceRate = profileBody?.customDistanceRate;
    const hasInteracDetails = Boolean((profileBody?.interacId || '').trim() && (profileBody?.securityAnswer || '').trim());
    const hasBankTransferDetails = Boolean((profileBody?.voidChequeFilename || '').trim());
    const hasPaymentDetails = hasInteracDetails || hasBankTransferDetails;
    const isMileageInvoiceData = (invoice = {}) => invoice.category === 'Travel' && invoice.subCategory === 'Mileage';
    const hasValidMileageTrips = (invoice = {}) => {
        if (!isMileageInvoiceData(invoice)) return true;
        const trips = Array.isArray(invoice.distanceTrips) ? invoice.distanceTrips : [];
        return trips.length > 0 && trips.every((trip) => {
            const distance = Number(trip?.distance);
            return (
                String(trip?.tripDate || '').trim() !== '' &&
                String(trip?.fromLocation || '').trim() !== '' &&
                String(trip?.toLocation || '').trim() !== '' &&
                Number.isFinite(distance) &&
                distance > 0
            );
        });
    };

    useEffect(() => {
        if (invoiceDataSubmitted) {
            setIsConfirmationModalOpen(true);
        }
    }, [invoiceDataSubmitted]);

    const {
        loading: editLoading,
        data: editInvoiceData,
        error: editInvoiceError,
    } = useQuery(GET_INVOICE, {
        variables: { invoiceID: routeInvoiceID },
        skip: !isEditMode,
    });

    useEffect(() => {
        if (!isEditMode) return;
        const invoice = editInvoiceData?.getInvoice?.body;
        if (!invoice) return;
        
        setEditOriginalInvoice(invoice);
        setRemoveExistingFile(false);
        const nextEditFormData = {
            expenseType: invoice.expenseType || '',
            category: invoice.category || '',
            subCategory: invoice.subCategory || '',
            project: invoice.project || '',
            distance: invoice.distance || '',
            entity: invoice.entity || '',
            allocation: invoice.allocation || '',
            amount: Number(invoice.originalAmount) || '',
            CADAmount: Number(invoice.CADAmount) || '',
            currency: invoice.currency || 'CAD',
            gst: invoice.GST || '',
            pst: invoice.PST || '',
            userNote: invoice.userNote || '',
            details: invoice.details || '',
            distanceTrips: Array.isArray(invoice.distanceTrips)
                ? invoice.distanceTrips.map((trip, index) => ({
                    sortOrder: trip.sortOrder ?? index,
                    tripDate: trip.tripDate ? String(trip.tripDate).slice(0, 10) : '',
                    fromLocation: trip.fromLocation || '',
                    toLocation: trip.toLocation || '',
                    distance: trip.distance ?? '',
                    notes: trip.notes || '',
                }))
                : [],
        };
        setEditInitialFormData(nextEditFormData);
        setEditFormData(nextEditFormData);
    }, [editInvoiceData, isEditMode]);

    useEffect(() => {
        if (!editInvoiceError) return;
        alert('Unable to load invoice details for editing.');
    }, [editInvoiceError]);

    const [modifyInvoiceMutation] = useMutation(MODIFY_INVOICE, {
        onError: () => {
            alert('Failed to modify invoice. Please try again.');
        }
    });

    const closeModal = () => {
        if (isEditMode) {
            navigate(`/details/${editOriginalInvoice?.category || 'Invoice'}/${routeInvoiceID}`);
        } else {
            window.location.reload();
        }
    };

    const returnToDashboard = () => {
        navigate('/');
    };

    const handleCancelEdit = () => {
        navigate(`/details/${editOriginalInvoice?.category || 'Invoice'}/${routeInvoiceID}`);
    };

    const base64ToUint8Array = (base64Input) => {
        const cleanBase64 = String(base64Input || '').replace(/^data:.*;base64,/, '');
        const binary = atob(cleanBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes;
    };

    const blobToUint8Array = (blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(new Uint8Array(reader.result));
        reader.onerror = () => reject(reader.error || new Error('Unable to read uploaded file.'));
        reader.readAsArrayBuffer(blob);
    });

    const resolveMimeType = (file) => {
        if (typeof file?.type === 'string' && file.type.trim() !== '') {
            return file.type.toLowerCase();
        }
        const dataUrl = typeof file?.url === 'string' ? file.url : (typeof file?.data === 'string' ? file.data : '');
        const dataUrlMatch = dataUrl.match(/^data:([^;]+);base64,/i);
        if (dataUrlMatch?.[1]) {
            return dataUrlMatch[1].toLowerCase();
        }
        const name = String(file?.name || '').toLowerCase();
        if (name.endsWith('.pdf')) return 'application/pdf';
        if (name.endsWith('.png')) return 'image/png';
        if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
        return '';
    };

    const readFileBytes = async (file) => {
        if (!file) {
            throw new Error('Missing uploaded file.');
        }
        if (file instanceof Uint8Array) {
            return file;
        }
        if (typeof file.arrayBuffer === 'function') {
            return new Uint8Array(await file.arrayBuffer());
        }
        if (typeof Blob !== 'undefined' && file instanceof Blob) {
            return blobToUint8Array(file);
        }
        if (typeof file?.url === 'string' && file.url.startsWith('data:')) {
            return base64ToUint8Array(file.url);
        }
        if (typeof file?.data === 'string' && file.data.startsWith('data:')) {
            return base64ToUint8Array(file.data);
        }
        if (typeof file?.base64 === 'string' && file.base64.trim() !== '') {
            return base64ToUint8Array(file.base64);
        }
        throw new Error(`Unsupported file object for ${file?.name || 'uploaded file'}.`);
    };

    async function convertImagesToPDF(imageFiles) {
        const pdfDoc = await PDFDocument.create();
        for (const file of imageFiles) {
            const mimeType = resolveMimeType(file);
            const fileBytes = await readFileBytes(file);
            if (mimeType === 'application/pdf') {
                const externalPDFDoc = await PDFDocument.load(fileBytes);
                const copiedPages = await pdfDoc.copyPages(externalPDFDoc, externalPDFDoc.getPageIndices());
                copiedPages.forEach(page => pdfDoc.addPage(page));
            } else {
                let img;
                if (mimeType === 'image/jpeg' || mimeType === 'image/jpg') {
                    img = await pdfDoc.embedJpg(fileBytes);
                } else if (mimeType === 'image/png') {
                    img = await pdfDoc.embedPng(fileBytes);
                } else {
                    alert(`Unsupported image format: ${file?.name || 'Unnamed file'}`);
                    continue;
                }
                const { width, height } = img.scale(1);
                const page = pdfDoc.addPage([width, height]);
                page.drawImage(img, {
                    x: 0,
                    y: 0,
                    width: width,
                    height: height,
                });
            }
        }
        return pdfDoc.save();
    }

    const bytesToBase64 = (bytes) => {
        let binary = '';
        const chunkSize = 0x8000;
        for (let i = 0; i < bytes.length; i += chunkSize) {
            binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
        }
        return btoa(binary);
    };

    const submitFiles = async () => {
        try {
            for (let i = 0; i < fileUploadData.length; i++) {
                const uploadedFiles = Array.isArray(fileUploadData[i]) ? fileUploadData[i] : [];
                if (uploadedFiles.length !== 0) {
                    if (!submitInvoiceFormData[i]) {
                        throw new Error(`Missing invoice form data for invoice ${i + 1}.`);
                    }
                    const pdfBytes = await convertImagesToPDF(uploadedFiles);
                    const pdfByteArray = new Uint8Array(pdfBytes);
                    const pdfBase64 = bytesToBase64(pdfByteArray);
                    const pdfName = (Math.random() + 1).toString(36).substring(2) + '.pdf';
                    submitInvoiceFormData[i].invoiceFilename = pdfName;
                    submitInvoiceFormData[i].invoiceFileContent = pdfBase64;
                    submitInvoiceFormData[i].invoiceFileSize = pdfByteArray.length;
                    submitInvoiceFormData[i].invoiceFileMimeType = 'application/pdf';
                }
            }
            return true;
        } catch (error) {
            console.log('File upload error:', error);
            alert('Failed to submit invoice files, please try again');
            return false;
        }
    };


    const [submitInvoice] = useMutation(UPLOAD_INVOICE, {
    onCompleted(data) {
        if (data.uploadInvoice.status.statusCode !== '200') {
            console.error('Invoice submission failed:', data.uploadInvoice.body);
        }
    },
    onError(error) {
        console.error('Invoice submission error:', error);
    }
});

const submitBatchInvoices = async () => {
    failedInvoicesRef.current = [];

    const promises = submitInvoiceFormData.map(async (invoice, index) => {
        try {
            const variables = {
                originalAmount: invoice.amount,
                CADAmount: invoice.CADAmount,
                currency: invoice.currency,
                gst: normalizeNumericInput(invoice.gst),
                pst: normalizeNumericInput(invoice.pst),
                details: invoice.details,
                expenseType: invoice.expenseType,
                category: invoice.category,
                subCategory: invoice.subCategory,
                project: invoice.project,
                distance: invoice.distance,
                distanceTrips: Array.isArray(invoice.distanceTrips) ? invoice.distanceTrips : [],
                entity: invoice.entity,
                allocation: invoice.allocation,
                userNote: invoice.userNote,
                date: invoice.date || new Date().toISOString().split('T')[0],
            };

            if (invoice.invoiceFilename) {
                variables.invoiceFilename = invoice.invoiceFilename;
            }
            if (invoice.invoiceFileContent) {
                variables.invoiceFileContent = invoice.invoiceFileContent;
                variables.invoiceFileSize = invoice.invoiceFileSize;
                variables.invoiceFileMimeType = invoice.invoiceFileMimeType;
            }

            const result = await submitInvoice({ variables });

            if (result.data?.uploadInvoice?.status?.statusCode !== '200') {
                failedInvoicesRef.current.push({
                    position: index + 1,
                    reason: result.data?.uploadInvoice?.body || 'Unknown error',
                });
            }

            return result;
        } catch (error) {
            console.error(`Invoice ${index + 1} error:`, error);
            failedInvoicesRef.current.push({
                position: index + 1,
                reason: error?.message || 'Unknown error',
            });
            return null;
        }
    });

    try {
        await Promise.all(promises);

        if (failedInvoicesRef.current.length === 0) {
            setInvoiceDataSubmitted(true);
        } else {
            // Report which invoice failed and why. The reason used to go only to the
            // console, which meant every failure needed a developer to diagnose.
            const details = failedInvoicesRef.current
                .map(({ position, reason }) => `Invoice ${position}: ${reason}`)
                .join('\n');
            const count = failedInvoicesRef.current.length;
            alert(`${count} invoice${count === 1 ? '' : 's'} could not be saved.\n\n${details}`);
        }
    } catch (error) {
        console.error('Error submitting invoices:', error);
        alert('An error occurred while submitting invoices. Please try again.');
    }
}

    const handleSubmitAll = async () => {
        if (!hasPaymentDetails) {
            alert(PAYMENT_DETAILS_REQUIRED_MESSAGE);
            return;
        }

        const filledInvoiceIndexes = submitInvoiceFormData.reduce((indexes, data, index) => {
            if (data && (
                (typeof data.expenseType === 'string' && data.expenseType.trim() !== '') ||
                (typeof data.category === 'string' && data.category.trim() !== '') ||
                (typeof data.subCategory === 'string' && data.subCategory.trim() !== '') ||
                (typeof data.currency === 'string' && data.currency.trim() !== '') ||
                (typeof data.amount === 'number' && data.amount > 0)
            )) {
                indexes.push(index);
            }
            return indexes;
        }, []);

        const filledInvoices = filledInvoiceIndexes.map((index) => submitInvoiceFormData[index]);

        if (filledInvoices.length === 0) {
            alert('No submissions, please fill in at least 1 submission.');
            return;
        }

        const invoicesMissingBill = filledInvoiceIndexes.filter((index) => {
            const uploadedFiles = Array.isArray(fileUploadData[index]) ? fileUploadData[index] : [];
            const hasBillUploaded = uploadedFiles.length > 0;
            const note = submitInvoiceFormData[index]?.userNote;
            const hasUserNote = typeof note === 'string' && note.trim() !== '';
            return !hasBillUploaded && !hasUserNote;
        });

        if (invoicesMissingBill.length > 0) {
            const which = invoicesMissingBill.map((index) => `Invoice ${index + 1}`).join(', ');
            alert(`${which}: no bill is uploaded. Upload one, or explain in User Note why there isn't one.`);
            return;
        }

        // Report exactly which invoice and which field is at fault. The old check
        // was a single boolean, so a user with every visible field filled in was
        // told to "fill in all required fields" with nothing to act on.
        const problems = filledInvoiceIndexes.reduce((errors, index) => {
            const data = submitInvoiceFormData[index] || {};
            const label = `Invoice ${index + 1}`;

            const missing = REQUIRED_TEXT_FIELDS
                .filter(([key]) => !(typeof data[key] === 'string' && data[key].trim() !== ''))
                .map(([, name]) => name);
            if (missing.length > 0) {
                errors.push(`${label}: missing ${missing.join(', ')}.`);
            }

            // A bad value and a blank one need different messages.
            [['GST', data.gst], ['PST', data.pst]].forEach(([name, value]) => {
                const message = numericInputError(value, name);
                if (message) {
                    errors.push(`${label}: ${message}`);
                }
            });

            const amountText = String(data.amountText ?? '').trim();
            const amountFormatError = numericInputError(amountText, 'Sub Total');
            if (amountText === '') {
                errors.push(`${label}: Sub Total is required.`);
            } else if (amountFormatError) {
                errors.push(`${label}: ${amountFormatError}`);
            } else if (!(Number(amountText) > 0)) {
                errors.push(`${label}: Sub Total must be greater than 0.`);
            } else if (data.CADAmount === null || data.CADAmount === '' || isNaN(Number(data.CADAmount))) {
                // Only reachable once the Sub Total itself is fine, so this really is
                // the currency conversion not having resolved.
                errors.push(`${label}: the CAD amount has not been calculated yet. Check the Currency and try again.`);
            }

            return errors;
        }, []);

        if (problems.length > 0) {
            alert(`Please correct the following before submitting:\n\n${problems.join('\n')}`);
            return;
        }

        const containsDistanceInvoice = filledInvoices.some((invoice) => {
            const dist = parseFloat(invoice?.distance);
            return Number.isFinite(dist) && dist > 0;
        });
        if (containsDistanceInvoice && !canSubmitDistanceInvoices) {
            alert('Distance-based invoices are disabled for your account.');
            return;
        }

        const hasInvalidMileageTrips = filledInvoices.some((invoice) => !hasValidMileageTrips(invoice));
        if (hasInvalidMileageTrips) {
            alert('Mileage invoices require at least one trip with date, from, to, and distance.');
            return;
        }

        const originalFormData = submitInvoiceFormData;
        setSubmitInvoiceFormData(filledInvoices);
        const successUpload = await submitFiles();
        if (successUpload) {
            await submitBatchInvoices();
        }
        setSubmitInvoiceFormData(originalFormData);
    };

    const handleSaveEditedInvoice = async () => {
        if (!editOriginalInvoice || !editFormData) {
            alert('Invoice data is not ready yet. Please wait.');
            return;
        }

        if (editOriginalInvoice.status !== 'EditRequired' && editOriginalInvoice.status !== 'Pending') {
            alert('This invoice is no longer editable. Only Pending or Edit Required invoices can be modified.');
            return;
        }

        // Same reporting as submission, so a save that is refused says why.
        const editProblems = REQUIRED_TEXT_FIELDS
            .filter(([key]) => !(typeof editFormData[key] === 'string' && editFormData[key].trim() !== ''))
            .map(([, name]) => `Missing ${name}.`);

        [['GST', editFormData.gst], ['PST', editFormData.pst]].forEach(([name, value]) => {
            const message = numericInputError(value, name);
            if (message) {
                editProblems.push(message);
            }
        });

        const editAmountText = String(editFormData.amountText ?? editFormData.amount ?? '').trim();
        const editAmountFormatError = numericInputError(editAmountText, 'Sub Total');
        if (editAmountText === '') {
            editProblems.push('Sub Total is required.');
        } else if (editAmountFormatError) {
            editProblems.push(editAmountFormatError);
        } else if (!(Number(editAmountText) > 0)) {
            editProblems.push('Sub Total must be greater than 0.');
        } else if (editFormData.CADAmount === null || editFormData.CADAmount === '' || isNaN(Number(editFormData.CADAmount))) {
            editProblems.push('The CAD amount has not been calculated yet. Check the Currency and try again.');
        }

        if (editProblems.length > 0) {
            alert(`Please correct the following before saving:\n\n${editProblems.join('\n')}`);
            return;
        }

        if (!hasValidMileageTrips(editFormData)) {
            alert('Mileage invoices require at least one trip with date, from, to, and distance.');
            return;
        }

        const editedDistance = parseFloat(editFormData?.distance);
        if (Number.isFinite(editedDistance) && editedDistance > 0 && !canSubmitDistanceInvoices) {
            alert('Distance-based invoices are disabled for your account.');
            return;
        }

        const fields = [
            ['originalAmount', 'amount'],
            ['CADAmount', 'CADAmount'],
            ['currency', 'currency'],
            ['GST', 'gst'],
            ['PST', 'pst'],
            ['details', 'details'],
            ['expenseType', 'expenseType'],
            ['category', 'category'],
            ['subCategory', 'subCategory'],
            ['project', 'project'],
            ['distance', 'distance'],
            ['entity', 'entity'],
            ['allocation', 'allocation'],
            ['userNote', 'userNote'],
        ];

        const modifications = {};
        const oldValues = {};

        fields.forEach(([invoiceField, formField]) => {
            const newValue = editFormData[formField] ?? null;
            const oldValue = editOriginalInvoice[invoiceField] ?? null;

            // Normalize number-like fields so string/number comparisons don't block updates.
            if (invoiceField === 'GST' || invoiceField === 'PST') {
                const normalizedNew = normalizeNumericInput(newValue);
                const normalizedOld = normalizeNumericInput(oldValue);
                if (normalizedNew !== normalizedOld) {
                    modifications[invoiceField] = normalizedNew;
                    oldValues[invoiceField] = normalizedOld;
                }
                return;
            }

            if (invoiceField === 'originalAmount' || invoiceField === 'CADAmount') {
                const normalizedNew = newValue === null || newValue === '' ? null : Number(newValue);
                const normalizedOld = oldValue === null || oldValue === '' ? null : Number(oldValue);
                if (normalizedNew !== normalizedOld) {
                    modifications[invoiceField] = normalizedNew;
                    oldValues[invoiceField] = normalizedOld;
                }
                return;
            }

            if (String(newValue ?? '') !== String(oldValue ?? '')) {
                modifications[invoiceField] = newValue;
                oldValues[invoiceField] = oldValue;
            }
        });

        const normalizedNewTrips = Array.isArray(editFormData.distanceTrips)
            ? editFormData.distanceTrips.map((trip, index) => ({
                sortOrder: index,
                tripDate: trip.tripDate || '',
                fromLocation: trip.fromLocation || '',
                toLocation: trip.toLocation || '',
                distance: trip.distance === '' || trip.distance === null || trip.distance === undefined ? null : Number(trip.distance),
                notes: trip.notes || '',
            }))
            : [];
        const normalizedOldTrips = Array.isArray(editOriginalInvoice.distanceTrips)
            ? editOriginalInvoice.distanceTrips.map((trip, index) => ({
                sortOrder: index,
                tripDate: trip.tripDate ? String(trip.tripDate).slice(0, 10) : '',
                fromLocation: trip.fromLocation || '',
                toLocation: trip.toLocation || '',
                distance: trip.distance === '' || trip.distance === null || trip.distance === undefined ? null : Number(trip.distance),
                notes: trip.notes || '',
            }))
            : [];
        if (JSON.stringify(normalizedNewTrips) !== JSON.stringify(normalizedOldTrips)) {
            modifications.distanceTrips = normalizedNewTrips;
            oldValues.distanceTrips = normalizedOldTrips;
        }

        const hasReplacementFiles = Array.isArray(editFileUploadData) && editFileUploadData.length > 0;
        if (hasReplacementFiles) {
            const pdfBytes = await convertImagesToPDF(editFileUploadData);
            const pdfByteArray = new Uint8Array(pdfBytes);
            const pdfBase64 = bytesToBase64(pdfByteArray);
            const pdfName = (Math.random() + 1).toString(36).substring(2) + '.pdf';
            modifications.invoiceFilename = pdfName;
            modifications.invoiceFileContent = pdfBase64;
            modifications.invoiceFileSize = pdfByteArray.length;
            modifications.invoiceFileMimeType = 'application/pdf';
            oldValues.invoiceFilename = editOriginalInvoice.invoiceFilename || null;
        } else if (removeExistingFile) {
            modifications.removeInvoiceFile = true;
            oldValues.invoiceFilename = editOriginalInvoice.invoiceFilename || null;
        }

        if (Object.keys(modifications).length === 0) {
            alert('No changes to save.');
            return;
        }

        modifications.invoiceID = routeInvoiceID.toString();
        modifications.oldValues = JSON.stringify(oldValues);
        modifications.originalAmount = Number(editFormData.amount).toString();
        modifications.currency = editFormData.currency;
        modifications.CADAmount = Number(editFormData.CADAmount).toString();
        if (isMileageInvoiceData(editFormData) || Array.isArray(modifications.distanceTrips)) {
            modifications.distanceTrips = normalizedNewTrips;
        }

        const result = await modifyInvoiceMutation({
            variables: modifications,
            refetchQueries: [
                {
                    query: GET_INVOICE,
                    variables: { invoiceID: routeInvoiceID },
                },
            ],
            awaitRefetchQueries: true,
        });
        if (result?.data?.modifyInvoice?.status?.statusCode === '200') {
            setInvoiceDataSubmitted(true);
            return;
        }
        alert('Failed to modify invoice. Please try again.');
    };

    const invoiceHasDraftData = (invoice = {}, uploadedFiles = []) => {
        const hasUploadedFiles = Array.isArray(uploadedFiles) && uploadedFiles.length > 0;
        const hasTextValue = (value) => String(value || '').trim() !== '';
        const hasNumberValue = (value) => {
            if (value === '' || value === null || value === undefined) return false;
            const numberValue = Number(value);
            return Number.isFinite(numberValue) && numberValue !== 0;
        };
        const hasMeaningfulTripData = Array.isArray(invoice?.distanceTrips) && invoice.distanceTrips.some((trip) => (
            hasTextValue(trip?.fromLocation) ||
            hasTextValue(trip?.toLocation) ||
            hasTextValue(trip?.notes) ||
            hasNumberValue(trip?.distance) ||
            hasTextValue(trip?.tripDate)
        ));
        const categoryChangedFromPrefill = hasTextValue(invoice?.category) && invoice.category !== prefillCategory;
        const subCategoryChangedFromPrefill = hasTextValue(invoice?.subCategory) && invoice.subCategory !== '';

        return (
            hasUploadedFiles ||
            hasTextValue(invoice?.details) ||
            hasTextValue(invoice?.userNote) ||
            hasNumberValue(invoice?.amount) ||
            hasNumberValue(invoice?.gst) ||
            hasNumberValue(invoice?.pst) ||
            hasTextValue(invoice?.expenseType) ||
            hasTextValue(invoice?.entity) ||
            hasTextValue(invoice?.allocation) ||
            categoryChangedFromPrefill ||
            subCategoryChangedFromPrefill ||
            hasMeaningfulTripData
        );
    };

    const handleDelete = (index) => {
        if (tabs.length <= 1) {
            alert('At least one invoice must remain in the list.');
            return;
        }

        const hasDraftData = invoiceHasDraftData(submitInvoiceFormData[index], fileUploadData[index]);
        if (hasDraftData && !window.confirm('This invoice has entered data or uploaded files. Delete it anyway?')) {
            return;
        }

        const currentActiveIndex = tabs.findIndex((tab) => tab.id === activeTab);
        const deletingActiveInvoice = currentActiveIndex === index;
        const updatedTabs = tabs.filter((_, tabIndex) => tabIndex !== index);
        const nextActiveTab = deletingActiveInvoice
            ? (index > 0 ? tabs[index - 1] : updatedTabs[0])
            : tabs[currentActiveIndex];
        const updatedFormData = submitInvoiceFormData.filter((_, dataIndex) => dataIndex !== index);
        const updatedFileData = fileUploadData.filter((_, dataIndex) => dataIndex !== index);

        setTabs(updatedTabs);
        setSubmitInvoiceFormData(updatedFormData);
        setFileUploadData(updatedFileData);
        setActiveTab(nextActiveTab?.id || 1);
    };

    const activeTabIndex = Math.max(0, tabs.findIndex((tab) => tab.id === activeTab));

    const handleFormSubmit = (data) => {
        // Functional update: the form publishes on every keystroke, so a copy taken
        // from the render closure is routinely stale and would drop other tabs' data.
        setSubmitInvoiceFormData((prevData) => {
            const updatedFormData = [...prevData];
            updatedFormData[activeTabIndex] = data;
            return updatedFormData;
        });

        // Tab titles are derived from the invoice details and are not editable, so
        // they are recomputed here rather than stored as user input.
        const detailsTitle = String(data?.details || '').trim();
        setTabs((prevTabs) =>
            prevTabs.map((tab, index) =>
                tab.id === activeTab
                    ? { ...tab, title: detailsTitle !== '' ? detailsTitle : `Default Invoice ${index + 1}` }
                    : tab
            )
        );

        if (data.expenseType) setPrefillExpenseType(data.expenseType);
        if (data.category) setPrefillCategory(data.category);
        if (data.project) setPrefillProject(data.project);
    };

    const handleEditFormSubmit = (data) => {
        setEditFormData(data);
    };

    const handleEditFileUpload = (data) => {
        setEditFileUploadData(data || []);
        if (data && data.length > 0) {
            setRemoveExistingFile(false);
        }
    };

    const handleFileUpload = (data) => {
        setFileUploadData((prevData) => {
            const updatedFileData = [...prevData];
            updatedFileData[activeTabIndex] = data;
            return updatedFileData;
        });
    };

    const addNewTab = () => {
        const nextTabId = tabs.reduce((maxId, tab) => Math.max(maxId, tab.id || 0), 0) + 1;
        const createdTab = { id: nextTabId, title: `Default Invoice ${nextTabId}` };
        setSubmitInvoiceFormData((prevData) => [
            ...prevData,
            { expenseType: prefillExpenseType, category: prefillCategory, project: prefillProject },
        ]);
        setFileUploadData((prevData) => [...prevData, []]);
        setTabs((prevTabs) => [...prevTabs, createdTab]);
        setActiveTab(createdTab.id);
    };

    const canSaveEdit = editOriginalInvoice?.status === 'EditRequired' || editOriginalInvoice?.status === 'Pending';
    const activeUploadedFiles = fileUploadData[activeTabIndex] || [];
    const hasActiveUploadedFiles = Array.isArray(activeUploadedFiles) && activeUploadedFiles.length > 0;

    if (isEditMode && editLoading) {
        return (
            <div className={styles.pageContainer}>
                <h2 className={styles.title}>Modify Invoice</h2>
                <p className={styles.editMeta}>Loading invoice...</p>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            <h2 className={styles.title}>
                {isEditMode ? `Modify Invoice #${routeInvoiceID}` : 'Submit an Invoice'}
            </h2>

            {isEditMode ? (
                <div>
                    <div className={styles.editMeta}>Status: {editOriginalInvoice?.status || 'Unknown'}</div>
                    <div className={styles.editNoteWrap}>
                        <div className={styles.editNoteTitle}>Edit Required Note</div>
                        <div className={styles.editNoteBody}>{editOriginalInvoice?.rejectNote || 'No note from approver.'}</div>
                    </div>
                    <div className={styles.editFileSection}>
                        <div className={styles.editFileHeader}>
                            <div className={styles.editMeta}>Replace uploaded file (optional)</div>
                            <div className={styles.editHeaderActions}>
                                <button
                                    className={styles.cancelEditButton}
                                    onClick={handleCancelEdit}
                                >
                                    Cancel
                                </button>
                                <button
                                    className={styles.saveEditButton}
                                    onClick={handleSaveEditedInvoice}
                                    disabled={!canSaveEdit}
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                        <FileUpload onSubmit={handleEditFileUpload} initialValues={editFileUploadData} />
                        <button
                            className={styles.removeFileButton}
                            onClick={() => {
                                setEditFileUploadData([]);
                                setRemoveExistingFile((prev) => !prev);
                            }}
                        >
                            {removeExistingFile ? 'Undo Remove Existing File' : 'Remove Existing File'}
                        </button>
                    </div>
                    <SubmitInvoiceForm
                        onSubmit={handleEditFormSubmit}
                        initialValues={editInitialFormData || {}}
                        prefillEnabled={true}
                        canSubmitDistanceInvoices={canSubmitDistanceInvoices}
                        customDistanceRate={customDistanceRate}
                    />
                </div>
            ) : (
                <div className={styles.invoiceWorkspace}>
                    <div className={styles.invoiceActionBar}>
                        <div className={styles.receiptControls}>
                            <div className={styles.uploadAction}>
                                <FileUpload
                                    onSubmit={handleFileUpload}
                                    initialValues={activeUploadedFiles}
                                    showPreviews={false}
                                    compact={true}
                                />
                            </div>
                            <button
                                type="button"
                                className={styles.viewDocumentButton}
                                disabled={!hasActiveUploadedFiles}
                                onClick={() => setIsDocumentDrawerOpen(true)}
                            >
                                View document{hasActiveUploadedFiles ? ` (${activeUploadedFiles.length})` : ''}
                            </button>
                        </div>
                    </div>
                    <div className={styles.formAndInvoicePanel}>
                        <div className={styles.formShell}>
                            <SubmitInvoiceForm
                                key={activeTab}
                                onSubmit={handleFormSubmit}
                                initialValues={submitInvoiceFormData[activeTabIndex]}
                                canSubmitDistanceInvoices={canSubmitDistanceInvoices}
                                customDistanceRate={customDistanceRate}
                            />
                        </div>
                        <InvoiceSubmissionTabMenu
                            tabs={tabs}
                            activeTab={activeTab}
                            handleDelete={handleDelete}
                            addNewTab={addNewTab}
                            handleSubmitAll={handleSubmitAll}
                            canSubmitInvoices={hasPaymentDetails}
                            paymentDetailsMessage={PAYMENT_DETAILS_REQUIRED_MESSAGE}
                            setActiveTab={setActiveTab}
                        />
                    </div>
                    <Modal
                        isOpen={isDocumentDrawerOpen}
                        onRequestClose={() => setIsDocumentDrawerOpen(false)}
                        className={styles.documentDrawer}
                        overlayClassName={styles.documentDrawerOverlay}
                        contentLabel="Uploaded document preview"
                    >
                        <div className={styles.documentDrawerHeader}>
                            <h3>Uploaded Document</h3>
                            <button
                                type="button"
                                className={styles.drawerCloseButton}
                                onClick={() => setIsDocumentDrawerOpen(false)}
                            >
                                Close
                            </button>
                        </div>
                        <FileUpload onSubmit={handleFileUpload} initialValues={activeUploadedFiles} />
                    </Modal>
                </div>
            )}
            <Modal
                isOpen={isConfirmationModalOpen}
                onRequestClose={closeModal}
                style={customModalStyles}
                contentLabel="Submission Result"
            >
                <button className={styles.closeButton} onClick={closeModal}>
                    <span className={styles.closeIcon}>x</span>
                </button>
                <h2 className={styles.successMessage}>
                    {isEditMode ? 'You have successfully updated the invoice.' : 'You have successfully submitted your invoices.'}
                </h2>
                {!isEditMode && (
                    <div className={styles.buttonContainer}>
                        <button className={styles.anotherInvoiceButton} onClick={closeModal}>
                            <CreateIcon className={styles.icon}></CreateIcon>
                            <div >Submit Another Invoice</div>
                        </button>
                        <button className={styles.toDashboardButton} onClick={returnToDashboard}>
                            <Home className={styles.icon}></Home>
                            <div> Return to Dashboard</div>
                        </button>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default SubmitInvoicePage;
